import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import {
  Box,
  CircularProgress,
  Grid,
  TextField,
  FormHelperText,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  Toolbar,
  Typography,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import { Block, Edit } from "@material-ui/icons";
import { StyledButton } from "src/components/CustomButtons/TgButton";
import * as FirestoreService from "src/views/viewAdministrador/metodosDePago/cuentasBancarias/services/firestore";

const EditBank = ({ cuenta, Transition }) => {
  const { enqueueSnackbar } = useSnackbar();
  const conjunto = useSelector((state) => state.complex.complex);
  const [open, setOpen] = useState(false);
  const [bancos, setBancos] = useState([]);
  const [formulario] = useState(cuenta);
  const [loading, setLoading] = useState(false);

  const TipoCuenta = [
    { id: 0, Nombre: "Ahorros" },
    { id: 1, Nombre: "Corriente" },
  ];
  const TipoIdentificacion = [
    { id: 0, Nombre: "Ruc" },
    { id: 1, Nombre: "Cédula" },
    { id: 2, Nombre: "Pasaporte" },
  ];

  useEffect(() => {
    const unsub = FirestoreService.getAllBanks({
      next: (querySnapshot) => {
        const items = querySnapshot.docs.map((docSnapshot) =>
          docSnapshot.data()
        );
        setBancos(items);
        setLoading(false);
      },
    });
    return () => {
      unsub();
    };
  }, []);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Tooltip title="Editar">
        <IconButton aria-label="editar" onClick={handleOpen}>
          <Edit />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <DialogTitle id="delete-dialog-title">
          <Toolbar>
            <Typography align="center" variant="h3">
              Crear Cuenta
            </Typography>
          </Toolbar>
        </DialogTitle>
        <Formik
          initialValues={{
            Banco: formulario.Banco,
            TipoCuenta: formulario.TipoCuenta,
            NombreCuenta: formulario.NombreCuenta || "",
            NumeroCuenta: formulario.NumeroCuenta || "",
            TipoIdentificacion: formulario.TipoIdentificacion,
            Identificacion: formulario.Identificacion || "",
            Correo: formulario.Correo || "",
          }}
          validationSchema={Yup.object().shape({
            Banco: Yup.string()
              .ensure()
              .trim()
              .required("¡Se requiere rellenar este campo!"),
            TipoCuenta: Yup.string()
              .ensure()
              .required("¡Se requiere rellenar este campo!"),
            TipoIdentificacion: Yup.string()
              .ensure()
              .required("¡Se requiere rellenar este campo!"),
            NombreCuenta: Yup.string()
              .ensure()
              .max(100, "¡Limite máximo de 100 caracteres!")
              .required("¡Se requiere rellenar este campo!"),
            NumeroCuenta: Yup.string()
              .ensure()
              .trim()
              .max(25, "¡Limite máximo de 25 caracteres!")
              .required("¡Se requiere rellenar este campo!")
              .matches(/^[0-9]+$/gm, "¡Solo se admiten números!"),
            Correo: Yup.string()
              .trim()
              .max(50, "¡Limite máximo de 50 caracteres!")
              .email("¡Ingrese un correo válido!"),
            Identificacion: Yup.string()
              .ensure()
              .trim()
              .max(20, "¡Limite máximo de 20 caracteres!")
              .required("¡Se requiere rellenar este campo!"),
          })}
          onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
            setLoading(true);
            try {
              await FirestoreService.updateCuenta(
                conjunto.id,
                values,
                formulario.id
              );
              setStatus({ success: true });

              enqueueSnackbar("Cuenta actualizada correctamente", {
                variant: "success",
              });
              handleClose();
            } catch (err) {
              setStatus({ success: false });
              setErrors({ submit: err.message });
            } finally {
              setSubmitting(false);
              setLoading(false);
            }
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
          }) =>
            loading ? (
              <Box display="flex" justifyContent="center" my={5}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                <DialogContent dividers>
                  <Grid container spacing={2}>
                    {errors.submit && (
                      <Grid item xs={12}>
                        <FormHelperText error>{errors.submit}</FormHelperText>
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Banco"
                        name="Banco"
                        onChange={handleChange}
                        select
                        disabled
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        value={values.Banco}
                        variant="outlined"
                      >
                        {bancos.map((banco) => (
                          <option
                            key={banco.Nombre}
                            value={banco.Nombre}
                            defaultValue=""
                          >
                            {banco.Nombre}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Tipo de cuenta"
                        name="TipoCuenta"
                        onChange={handleChange}
                        select
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        value={values.TipoCuenta}
                        variant="outlined"
                      >
                        {TipoCuenta.map((tipoCuenta) => (
                          <option
                            key={tipoCuenta.Nombre}
                            value={tipoCuenta.Nombre}
                            defaultValue=""
                          >
                            {tipoCuenta.Nombre}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        error={Boolean(
                          touched.NumeroCuenta && touched.NombreCuenta
                        )}
                        helperText={touched.NumeroCuenta && errors.NumeroCuenta}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.NumeroCuenta}
                        label="Número de cuenta"
                        name="NumeroCuenta"
                        id="NumeroCuenta"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        error={Boolean(touched.Correo && touched.NombreCuenta)}
                        helperText={touched.NombreCuenta && errors.NombreCuenta}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.NombreCuenta}
                        label="Nombre del titular"
                        name="NombreCuenta"
                        id="NombreCuenta"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Tipo de identificación"
                        name="TipoIdentificacion"
                        onChange={handleChange}
                        select
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        value={values.TipoIdentificacion}
                        variant="outlined"
                      >
                        {TipoIdentificacion.map((tipoIdeniIficacion) => (
                          <option
                            key={tipoIdeniIficacion.Nombre}
                            value={tipoIdeniIficacion.Nombre}
                            defaultValue=""
                          >
                            {tipoIdeniIficacion.Nombre}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        error={Boolean(
                          touched.Identificacion && errors.Identificacion
                        )}
                        helperText={
                          touched.Identificacion && errors.Identificacion
                        }
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.Identificacion}
                        label="Identificación"
                        name="Identificacion"
                        id="Identificacion"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        error={Boolean(touched.Correo && errors.Correo)}
                        helperText={touched.Correo && errors.Correo}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.Correo}
                        label="Correo electrónico"
                        name="Correo"
                        id="Correo"
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <StyledButton
                    variant="outlined"
                    autoFocus
                    onClick={handleClose}
                    color="primary"
                    startIcon={<Block fontSize="small" />}
                  >
                    Cancelar
                  </StyledButton>
                  <StyledButton
                    color="primary"
                    disabled={isSubmitting}
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    <SaveIcon className="me-2" />
                    Actualizar
                  </StyledButton>
                </DialogActions>
              </form>
            )
          }
        </Formik>
      </Dialog>
    </>
  );
};

export default EditBank;
